/**
 * The bookkeeping voice needs so both ends of a call agree on which call it is.
 *
 * A peer connection's DTLS keys belong to it alone: an offer from a *new*
 * connection cannot be applied to an old one, and the answer is silence. Two
 * ways a side ends up with a new connection while the other keeps its old one:
 *
 *   - a reload — the page, and its connection, are new;
 *   - a rebuild on one side only — which used to happen on a socket blip, when
 *     the side that stayed restarted its call and its fresh offer landed on the
 *     partner's surviving connection.
 *
 * So every signal names the connection that sent it (`session`) and the one it
 * was meant for (`for`), and `SessionGate` decides what to do with each:
 *
 *   - meant for a connection of ours that is gone   -> drop it
 *   - from a connection of theirs we already left    -> drop it
 *   - an offer from a connection we have not met yet -> rebuild ours, then take it
 *   - anything else                                  -> deliver it
 *
 * A rebuild therefore happens only when the other side has actually started a
 * new connection, on whichever side that was, and a socket blip that leaves
 * both connections alive rebuilds nothing. A signal with no session — an older
 * client — is delivered exactly as before.
 *
 * `SignalInbox` holds what arrives before this page has a connection to give it
 * to: the side that stayed offers the moment it notices the partner back, and an
 * offer dropped there is half of a perfect-negotiation deadlock.
 *
 * Pure apart from what it is handed, so both are tested without a browser.
 */

import type { PlayerId, RtcSignal } from '@overcooked/shared'

/** A fresh name for a new connection. Unique enough: two per call, never compared across rooms. */
export function newSessionId(random: () => number = Math.random): string {
  return `${Date.now().toString(36)}-${Math.floor(random() * 2 ** 48).toString(36)}`
}

export type Admission = 'deliver' | 'drop' | 'rebuild'

/** One side's view of one call: its own connection, and the other side's. */
export class SessionGate {
  /** The other side's connection, once it has said anything. */
  remote: string | null = null

  /**
   * @param local this side's connection.
   * @param dead the other side's connections this side has already moved on
   *   from. Carried from gate to gate across rebuilds, so a late signal from a
   *   connection that was replaced cannot trigger a second rebuild.
   */
  constructor(
    readonly local: string,
    readonly dead: ReadonlySet<string> = new Set(),
  ) {}

  /** The envelope this side puts on everything it sends. */
  stamp<T extends RtcSignal>(signal: T): T {
    return { ...signal, session: this.local, for: this.remote }
  }

  admit(signal: RtcSignal): Admission {
    // Addressed to a connection of ours that has been replaced: whatever it
    // says belongs to a negotiation that no longer exists here.
    if (signal.for && signal.for !== this.local) return 'drop'
    const from = signal.session
    // An older client names no connection; take it as it always was taken.
    if (!from) return 'deliver'
    if (this.dead.has(from)) return 'drop'
    if (signal.kind === 'bye') return 'deliver'
    if (this.remote === null) {
      this.remote = from
      return 'deliver'
    }
    if (from === this.remote) return 'deliver'
    // A connection on the other side we have never heard from. Only an offer
    // starts a call; a stray candidate or answer from it has nothing here to
    // belong to.
    if (signal.kind === 'description' && signal.description.type === 'offer') return 'rebuild'
    return 'drop'
  }

  /** The gate for this side's replacement connection, which has met nobody yet. */
  successor(local: string): SessionGate {
    const dead = new Set(this.dead)
    if (this.remote) dead.add(this.remote)
    return new SessionGate(local, dead)
  }
}

/** No more than this many signals are held for a page with nobody listening. */
export const INBOX_LIMIT = 256

/**
 * How long a held signal stays worth delivering. An offer older than this was
 * made by a connection that has long since restarted ICE or given up, and
 * feeding it to a fresh connection only starts a negotiation nobody is in.
 */
export const INBOX_MAX_AGE_MS = 30_000

/**
 * Signals that arrived while nothing was listening, in arrival order.
 *
 * A `bye` from a peer ends whatever that peer was negotiating, so everything
 * held from it before the `bye` is dropped with it, and anything older than
 * `INBOX_MAX_AGE_MS` is dropped when the inbox is drained.
 */
export class SignalInbox {
  private held: Array<{ from: PlayerId; signal: RtcSignal; at: number }> = []

  constructor(private readonly clock: () => number = () => Date.now()) {}

  hold(from: PlayerId, signal: RtcSignal): void {
    if (signal.kind === 'bye') {
      this.held = this.held.filter((entry) => entry.from !== from)
      return
    }
    this.held.push({ from, signal, at: this.clock() })
    if (this.held.length > INBOX_LIMIT) this.held.splice(0, this.held.length - INBOX_LIMIT)
  }

  /** Everything held and still fresh, oldest first, and an empty inbox afterwards. */
  drain(): Array<{ from: PlayerId; signal: RtcSignal }> {
    const now = this.clock()
    const out = this.held.filter((entry) => now - entry.at <= INBOX_MAX_AGE_MS)
    this.held = []
    return out.map(({ from, signal }) => ({ from, signal }))
  }

  get size(): number {
    return this.held.length
  }
}
